import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore, api } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  CalendarOff, 
  Plus, 
  Trash2, 
  Loader2, 
  Building2,
  CalendarDays,
  RefreshCw,
  Info,
  List,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths, getDay } from 'date-fns';
import { tr, enUS, nl, fr } from 'date-fns/locale';

const getLocale = (lang) => {
  switch (lang) {
    case 'tr': return tr;
    case 'nl': return nl;
    case 'fr': return fr;
    default: return enUS;
  }
};

export const LockedDates = () => {
  const { t, language } = useTranslation();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAnyAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN';
  const canFilterFirm = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN';
  
  const [loading, setLoading] = useState(true);
  const [lockedDates, setLockedDates] = useState([]);
  const [firms, setFirms] = useState([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, record: null });
  
  // View mode state
  const [viewMode, setViewMode] = useState('calendar'); // 'list' or 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDayInfo, setSelectedDayInfo] = useState({ open: false, date: null, records: [] });
  
  // Create form state
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [reason, setReason] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [creating, setCreating] = useState(false);

  const locale = getLocale(language);

  useEffect(() => {
    fetchLockedDates();
    if (canFilterFirm) {
      fetchFirms();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLockedDates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/locked-dates');
      if (response.data.success) {
        setLockedDates(response.data.data || []);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchFirms = async () => {
    try {
      const response = await api.get('/firms/');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch firms:', error);
    }
  };

  const handleCreate = async () => {
    if (selectedDates.length === 0) {
      toast.error(t('leave.selectDays'));
      return;
    }
    if (!reason.trim()) {
      toast.error(t('validation.required'));
      return;
    }

    setCreating(true);
    try {
      const dates = selectedDates.map(d => format(d, 'yyyy-MM-dd'));
      const response = await api.post('/admin/locked-dates', {
        firm_ids: canFilterFirm ? selectedFirms : [],
        dates,
        reason: reason.trim(),
        is_recurring_yearly: isRecurring
      });

      if (response.data.success) {
        toast.success(t('lockedDates.datesCreated'));
        setCreateDialog(false);
        resetForm();
        fetchLockedDates();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.record) return;

    try {
      const response = await api.delete(`/admin/locked-dates/${deleteDialog.record.id}`);
      if (response.data.success) {
        toast.success(t('lockedDates.datesDeleted'));
        setDeleteDialog({ open: false, record: null });
        fetchLockedDates();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const resetForm = () => {
    setSelectedDates([]);
    setSelectedFirms([]);
    setReason('');
    setIsRecurring(false);
  };

  const toggleFirmSelection = (firmId) => {
    if (selectedFirms.includes(firmId)) {
      setSelectedFirms(selectedFirms.filter(f => f !== firmId));
    } else {
      setSelectedFirms([...selectedFirms, firmId]);
    }
  };

  // Build a map of locked dates for quick lookup
  const lockedDatesMap = useMemo(() => {
    const map = new Map();
    lockedDates.forEach(record => {
      record.dates.forEach(dateStr => {
        const existing = map.get(dateStr) || [];
        existing.push(record);
        map.set(dateStr, existing);
      });
    });
    return map;
  }, [lockedDates]);

  // Get days for calendar view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const startDate = monthStart;
    const endDate = monthEnd;
    
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Add padding days for the start of the week
    const startDayOfWeek = getDay(monthStart);
    const paddingDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Monday start
    const paddedDays = [];
    
    for (let i = paddingDays; i > 0; i--) {
      const prevDay = new Date(monthStart);
      prevDay.setDate(prevDay.getDate() - i);
      paddedDays.push({ date: prevDay, isCurrentMonth: false });
    }
    
    days.forEach(day => {
      paddedDays.push({ date: day, isCurrentMonth: true });
    });
    
    // Fill remaining days to complete the grid
    const remainingDays = 42 - paddedDays.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const nextDay = new Date(monthEnd);
      nextDay.setDate(nextDay.getDate() + i);
      paddedDays.push({ date: nextDay, isCurrentMonth: false });
    }
    
    return paddedDays;
  }, [calendarMonth]);

  // Get locked records for a specific day
  const getLockedRecordsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return lockedDatesMap.get(dateStr) || [];
  };

  // Handle day click in calendar
  const handleDayClick = (date, records) => {
    if (records.length > 0) {
      setSelectedDayInfo({ open: true, date, records });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locked-dates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <CalendarOff className="inline-block h-6 w-6 mr-2 text-red-500" />
            {t('lockedDates.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('lockedDates.description')}
          </p>
        </div>
        <div className="flex gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className="gap-1"
            >
              <CalendarIcon className="h-4 w-4" />
              {t('lockedDates.calendarView') || 'Takvim'}
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-1"
            >
              <List className="h-4 w-4" />
              {t('lockedDates.listView') || 'Liste'}
            </Button>
          </div>
          <Button variant="outline" size="icon" onClick={fetchLockedDates}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => setCreateDialog(true)} data-testid="add-locked-dates-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('lockedDates.lockDate')}
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">{t('lockedDates.howItWorks')}</p>
              <ul className="mt-1 space-y-1 text-blue-700">
                <li>• {t('lockedDates.infoLine1')}</li>
                <li>• {t('lockedDates.infoLine2')}</li>
                {canFilterFirm && <li>• {t('lockedDates.infoLine3')}</li>}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-red-500" />
                {t('lockedDates.calendarTitle') || 'Kilitli Tarihler Takvimi'}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-medium text-lg min-w-[150px] text-center">
                  {format(calendarMonth, 'MMMM yyyy', { locale })}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {[
                t('calendar.monday') || 'Pzt',
                t('calendar.tuesday') || 'Sal',
                t('calendar.wednesday') || 'Çar',
                t('calendar.thursday') || 'Per',
                t('calendar.friday') || 'Cum',
                t('calendar.saturday') || 'Cmt',
                t('calendar.sunday') || 'Paz'
              ].map((day, i) => (
                <div key={i} className="text-center text-sm font-medium text-slate-500 dark:text-slate-400 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map(({ date, isCurrentMonth }, idx) => {
                const lockedRecords = getLockedRecordsForDay(date);
                const isLocked = lockedRecords.length > 0;
                const today = isToday(date);
                
                return (
                  <div
                    key={idx}
                    className={`
                      min-h-[80px] p-1 border rounded-lg cursor-pointer transition-all
                      ${!isCurrentMonth ? 'bg-slate-50 dark:bg-slate-900 opacity-50' : 'bg-white dark:bg-slate-800'}
                      ${today ? 'ring-2 ring-blue-500' : ''}
                      ${isLocked ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 hover:bg-red-100' : 'hover:bg-slate-50 dark:hover:bg-slate-700'}
                    `}
                    onClick={() => handleDayClick(date, lockedRecords)}
                  >
                    <div className={`text-sm font-medium mb-1 ${today ? 'text-blue-600' : ''} ${!isCurrentMonth ? 'text-slate-400' : ''}`}>
                      {format(date, 'd')}
                    </div>
                    {isLocked && (
                      <div className="space-y-1">
                        {lockedRecords.slice(0, 2).map((record, i) => (
                          <div
                            key={i}
                            className="text-xs bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded px-1 py-0.5 truncate"
                            title={record.reason}
                          >
                            <CalendarOff className="h-3 w-3 inline mr-1" />
                            {record.reason.length > 15 ? record.reason.substring(0, 15) + '...' : record.reason}
                          </div>
                        ))}
                        {lockedRecords.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{lockedRecords.length - 2} {t('lockedDates.more') || 'daha'}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div>
                <span className="text-sm text-slate-600 dark:text-slate-300">{t('lockedDates.lockedDay') || 'Kilitli Gün'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-white border border-slate-200 rounded ring-2 ring-blue-500"></div>
                <span className="text-sm text-slate-600 dark:text-slate-300">{t('lockedDates.today') || 'Bugün'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Locked Dates Table (List View) */}
      {viewMode === 'list' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('lockedDates.activeLockedDates')}</CardTitle>
            <CardDescription>
              {lockedDates.length} {t('lockedDates.recordsFound')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('lockedDates.dates')}</TableHead>
                  <TableHead>{t('lockedDates.reason')}</TableHead>
                  {canFilterFirm && <TableHead>{t('lockedDates.firms')}</TableHead>}
                  <TableHead>{t('lockedDates.repeat')}</TableHead>
                  <TableHead>{t('lockedDates.createdBy')}</TableHead>
                  <TableHead className="w-20">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lockedDates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canFilterFirm ? 6 : 5} className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <CalendarOff className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      {t('lockedDates.noLockedDates')}
                    </TableCell>
                  </TableRow>
                ) : (
                  lockedDates.map(record => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {record.dates.slice(0, 5).map(date => (
                          <Badge key={date} variant="outline" className="text-xs">
                            <CalendarDays className="h-3 w-3 mr-1" />
                            {format(parseISO(date), 'dd MMM yyyy', { locale })}
                          </Badge>
                        ))}
                        {record.dates.length > 5 && (
                          <Badge variant="secondary" className="text-xs">
                            +{record.dates.length - 5} {t('lockedDates.more')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="max-w-xs truncate" title={record.reason}>
                        {record.reason}
                      </p>
                    </TableCell>
                    {canFilterFirm && (
                      <TableCell>
                        {record.firm_ids?.length === 0 ? (
                          <Badge className="bg-purple-100 text-purple-800">{t('lockedDates.allFirms')}</Badge>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {record.firm_names?.map((name, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                <Building2 className="h-3 w-3 mr-1" />
                                {name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {record.is_recurring_yearly ? (
                        <Badge className="bg-green-100 text-green-800">{t('lockedDates.everyYear')}</Badge>
                      ) : (
                        <Badge variant="outline">{t('lockedDates.oneTime')}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 dark:text-slate-400">
                      {record.created_by_name}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteDialog({ open: true, record })}
                        data-testid={`delete-locked-${record.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {/* Day Info Dialog (for calendar view) */}
      <Dialog open={selectedDayInfo.open} onOpenChange={(open) => setSelectedDayInfo({ ...selectedDayInfo, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarOff className="h-5 w-5 text-red-500" />
              {selectedDayInfo.date && format(selectedDayInfo.date, 'd MMMM yyyy', { locale })}
            </DialogTitle>
            <DialogDescription>
              {selectedDayInfo.records.length} {t('lockedDates.lockedRecords') || 'kilitli kayıt'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {selectedDayInfo.records.map((record, idx) => (
              <div key={idx} className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-red-800 dark:text-red-200">{record.reason}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {canFilterFirm && (
                        record.firm_ids?.length === 0 ? (
                          <Badge className="bg-purple-100 text-purple-800">{t('lockedDates.allFirms')}</Badge>
                        ) : (
                          record.firm_names?.map((name, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              <Building2 className="h-3 w-3 mr-1" />
                              {name}
                            </Badge>
                          ))
                        )
                      )}
                      {record.is_recurring_yearly && (
                        <Badge className="bg-green-100 text-green-800">{t('lockedDates.everyYear')}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {t('lockedDates.createdBy')}: {record.created_by_name}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-100"
                    onClick={() => {
                      setSelectedDayInfo({ ...selectedDayInfo, open: false });
                      setDeleteDialog({ open: true, record });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('lockedDates.addNewTitle')}</DialogTitle>
            <DialogDescription>
              {t('lockedDates.addNewDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Calendar */}
            <div>
              <Label className="mb-2 block">{t('lockedDates.selectDates')}</Label>
              <div className="border rounded-lg p-2">
                <Calendar
                  mode="multiple"
                  selected={selectedDates}
                  onSelect={setSelectedDates}
                  locale={locale}
                  className="rounded-md"
                  disabled={(date) => date < new Date()}
                />
              </div>
              {selectedDates.length > 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  {selectedDates.length} {t('lockedDates.datesSelected')}
                </p>
              )}
            </div>

            {/* Options */}
            <div className="space-y-4">
              {/* Firm Selection (Super Admin only) */}
              {canFilterFirm && (
                <div>
                  <Label className="mb-2 block">{t('lockedDates.firms')}</Label>
                  <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                    <div 
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        selectedFirms.length === 0 ? 'bg-purple-100' : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800'
                      }`}
                      onClick={() => setSelectedFirms([])}
                    >
                      <input 
                        type="radio" 
                        checked={selectedFirms.length === 0} 
                        readOnly 
                        className="h-4 w-4"
                      />
                      <span className="font-medium">{t('lockedDates.allFirms')}</span>
                    </div>
                    <hr />
                    {firms.map(firm => (
                      <div 
                        key={firm.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          selectedFirms.includes(firm.id) ? 'bg-blue-50' : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800'
                        }`}
                        onClick={() => toggleFirmSelection(firm.id)}
                      >
                        <input 
                          type="checkbox" 
                          checked={selectedFirms.includes(firm.id)} 
                          readOnly 
                          className="h-4 w-4"
                        />
                        <Building2 className="h-4 w-4 text-slate-400" />
                        <span>{firm.name}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {selectedFirms.length === 0 
                      ? t('lockedDates.allFirmsApplies')
                      : `${selectedFirms.length} ${t('lockedDates.firmsSelected')}`}
                  </p>
                </div>
              )}

              {/* Reason */}
              <div>
                <Label htmlFor="reason">{t('lockedDates.reason')}</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('lockedDates.reasonPlaceholder')}
                  className="mt-1"
                  rows={3}
                />
              </div>

              {/* Recurring */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="recurring" className="cursor-pointer">{t('lockedDates.repeatYearly')}</Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('lockedDates.repeatYearlyDesc')}
                  </p>
                </div>
                <Switch
                  id="recurring"
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={creating || selectedDates.length === 0 || !reason.trim()}
            >
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('lockedDates.lockDates')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, record: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('lockedDates.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('lockedDates.deleteConfirm')} ({deleteDialog.record?.dates?.length} {t('lockedDates.dates').toLowerCase()})
            </DialogDescription>
          </DialogHeader>
          
          {deleteDialog.record && (
            <div className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                <strong>{t('lockedDates.reason')}:</strong> {deleteDialog.record.reason}
              </p>
              <div className="flex flex-wrap gap-1">
                {deleteDialog.record.dates.slice(0, 10).map(date => (
                  <Badge key={date} variant="outline" className="text-xs">
                    {format(parseISO(date), 'dd MMM yyyy', { locale })}
                  </Badge>
                ))}
                {deleteDialog.record.dates.length > 10 && (
                  <Badge variant="secondary">+{deleteDialog.record.dates.length - 10} {t('lockedDates.more')}</Badge>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, record: null })}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LockedDates;
